import React, {
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { sendMessageToAiAssistant } from 'util/sendMessageToAiAssistant';
import { useSize } from 'util/useSize';
import { getLocale } from 'i18n/locale';
import { getPromptsByTemplateId } from 'util/getPromptsByTemplateId';
import { ELocale } from 'i18n/localeEnum';
import styles from './style.module.scss';

interface AiAssistantWidgetProps {
  name?: string;
  templateId?: string;
  suggestions?: string[];
  margin?: {
    marginTop?: string;
    marginBottom?: string;
  };
}

export const AiAssistantWidget = ({
  name,
  templateId,
  suggestions: manualSuggestions = [],
  margin,
}: AiAssistantWidgetProps) => {
  const [inputValue, setInputValue] = useState('');
  const [shouldScrollSuggestions, setShouldScrollSuggestions] = useState(false);
  const [showSuggestionsFade, setShowSuggestionsFade] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const locale = getLocale() as ELocale;
  const isSupportedLocale = locale === ELocale.EN_US || locale === ELocale.EN_GB;

  const suggestions = React.useMemo(() => {
    if (!isSupportedLocale) return [];
    if (manualSuggestions.length > 0) return manualSuggestions;

    if (templateId) {
      return getPromptsByTemplateId(templateId, name) || [];
    }

    return [];
  }, [isSupportedLocale, manualSuggestions, templateId, name]);

  const suggestionsSize = useSize(suggestionsRef);

  if (!isSupportedLocale) {
    return null;
  }

  useLayoutEffect(() => {
    const textareaElement = textareaRef.current;
    if (!textareaElement) return;

    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const maxHeight = 6 * rootFontSize;

    textareaElement.style.height = 'auto';
    textareaElement.style.height = `${Math.min(textareaElement.scrollHeight, maxHeight)}px`;
    const shouldScroll = textareaElement.scrollHeight > maxHeight;

    textareaElement.style.overflowY = shouldScroll ? 'auto' : 'hidden';

    if (shouldScroll) {
      requestAnimationFrame(() => {
        textareaElement.scrollTo({
          top: textareaElement.scrollHeight,
          behavior: 'smooth',
        });
      });
    }
  }, [inputValue]);

  useLayoutEffect(() => {
    const suggestionsElement = suggestionsRef.current;
    if (!suggestionsElement) return;

    const suggestionButtons = Array.from(suggestionsElement.children) as HTMLElement[];
    if (!suggestionButtons.length) {
      setShouldScrollSuggestions(false);
      return;
    }

    const { columnGap } = getComputedStyle(suggestionsElement);
    const gap = Number.parseFloat(columnGap) || 0;
    const totalSuggestionsWidth = suggestionButtons.reduce((totalWidth, button) => (
      totalWidth + button.offsetWidth
    ), 0) + (gap * Math.max(suggestionButtons.length - 1, 0));

    setShouldScrollSuggestions(totalSuggestionsWidth > suggestionsElement.clientWidth);
  }, [suggestions, suggestionsSize?.width]);

  useLayoutEffect(() => {
    const suggestionsElement = suggestionsRef.current;
    if (!suggestionsElement || !shouldScrollSuggestions) {
      setShowSuggestionsFade(false);
      return undefined;
    }

    const updateFadeVisibility = () => {
      setShowSuggestionsFade(suggestionsElement.scrollLeft <= 1);
    };

    updateFadeVisibility();
    suggestionsElement.addEventListener('scroll', updateFadeVisibility, { passive: true });

    const cleanup = () => {
      suggestionsElement.removeEventListener('scroll', updateFadeVisibility);
    };

    return cleanup;
  }, [shouldScrollSuggestions, suggestions, suggestionsSize?.width]);

  const handleSend = () => {
    if (inputValue.trim()) {
      sendMessageToAiAssistant(inputValue);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (text: string) => {
    sendMessageToAiAssistant(text);
  };

  return (
    <div
      className={styles.widget}
      style={margin ? { marginTop: margin.marginTop, marginBottom: margin.marginBottom } : undefined}
    >
      <div className={styles.header}>
        <img
          className={styles.logo}
          src="/assets/ai-assistant-logo-blue.svg"
          alt="SkiGPT"
        />
        <h3 className={styles.title}>Try SkiGPT</h3>
        <p className={styles.description}>
          A faster, friendlier way to get snow reports, live cams, and resort info.
        </p>
      </div>

      <div className={styles.searchBox}>
        <textarea
          ref={textareaRef}
          className={styles.searchInput}
          placeholder={name ? `Ask SkiGPT about ${name}` : 'Ask SkiGPT'}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={450}
          rows={1}
        />
        <button type="button" className={styles.sendButton} onClick={handleSend}>
          <img src="/assets/icons/send-icon.svg" alt="Send message to SkiGPT" />
        </button>
      </div>

      {suggestions.length > 0 && (
        <div className={styles.suggestionsViewport}>
          <div
            ref={suggestionsRef}
            className={`${styles.suggestions} ${shouldScrollSuggestions ? styles.suggestionsScrollable : ''}`.trim()}
          >
            {suggestions.map((text) => (
              <button
                key={text}
                type="button"
                className={styles.pill}
                onClick={() => handleSuggestionClick(text)}
              >
                {text}
              </button>
            ))}
          </div>

          <div
            className={`${styles.suggestionsFade} ${showSuggestionsFade ? styles.suggestionsFadeVisible : ''}`.trim()}
            aria-hidden="true"
          >
            <img
              className={styles.suggestionsFadeArrow}
              src="/assets/icons/simple-arrow-blue.svg"
              alt=""
            />
          </div>
        </div>
      )}
    </div>
  );
};
